import { PUBLIC_ROOT_API } from '@/config/app.config';
import ApiService from './api.service';
import jwt_decode from 'jwt-decode';
import router from '@/router';
import { GetterTypes, MutationTypes } from '@/store-vuex/types';

/**
 * Manage how the Access Tokens are being stored and retrieved from storage.
 *
 * Current implementation stores to in memory store and cookie.
 */
export default class TokenService {
  public static readonly TOKEN_KEY = 'access_token';
  public static readonly REFRESH_TOKEN_KEY = 'refresh_token';
  public static readonly CSRF_TOKEN = 'csrf_token';
  public static readonly XSRF_TOKEN = 'xsrf_token';
  public static readonly X_XSRF_TOKEN = 'x-xsrf-token';

  /**
   * Get the JWT token from cookie or store
   * @returns {string} The token
   */
  public static getToken(): string {
    if (!router.app.$store.getters[GetterTypes.token]) {
      // Get Token from cookie

      const cookieToken = this.getCookieByName(this.TOKEN_KEY);
      if (!!cookieToken) {
        // Get expiration timestamp from token

        const decodedToken = jwt_decode(cookieToken) as any;
        const key = 'exp';
        const timestampExp = decodedToken[key] as number;

        router.app.$store.commit(MutationTypes.setToken, cookieToken);
        router.app.$store.commit(MutationTypes.setTokenTimestamp, timestampExp);
      } else {
        this.removeToken();
      }
    }
    return router.app.$store.getters[GetterTypes.token];
  }

  /**
   * Refresh the JWT token from cookie
   * @returns {string} The token
   */
  public static refreshToken(): void {
    const cookieToken = this.getCookieByName(this.TOKEN_KEY);
    router.app.$store.commit(MutationTypes.setToken, cookieToken);
  }


  /**
   * Get the XSRF token from cookie and set it as header for axios
   */
  public static setXsrfTokenInHeader(): void {
    const cookieToken = this.getCookieByName(this.XSRF_TOKEN);
    if (!!cookieToken) {
      ApiService.setHeader(this.X_XSRF_TOKEN, cookieToken);
    }
  }

  /**
   * Generate the XSRF token
   */
  public static async generateXsrfToken(): Promise<boolean> {
    const requestData = {
      url: `${PUBLIC_ROOT_API}/Tokens/csrf`,
    };

    return await ApiService.get(requestData.url)
      .then((response: any) => {
        return true;
      });
  }

  /**
   * Check JWT token existence from the cookie
   * @returns {boolean} True if the JWT token exists in the cookie
   */
  public static tokenExists(): boolean {
    return !!this.getToken();
  }

  /**
   * Check JWT token validity
   * @returns {boolean} True if the JWT token is valid
   */
  public static async isTokenValid(): Promise<boolean> {
    const requestData = {
      url: `${PUBLIC_ROOT_API}/Tokens/is-valid`,
    };

    // use another axios instance without interceptors in order to not show any error message
    const axiosInstance = ApiService.create();
    return (await axiosInstance).get(requestData.url)
    .then((response: any) => {
      return true;
    })
    .catch((error: any) => {
      return false;
    });
  }

  /**
   * Save the given JWT token
   * @param accessToken The token
   */
  public static saveToken(accessToken: string) {
    router.app.$store.commit(MutationTypes.setToken, accessToken);
  }

  /**
   * Remove/delete the JWT token and reset its expiration timestamp
   */
  public static removeToken() {
    router.app.$store.commit(MutationTypes.setToken, '');
    router.app.$store.commit(MutationTypes.setTokenTimestamp, 0);
  }

  /**
   * Get a cookie by name (only non secure and non http cookie can be retrieved)
   * @param name The cookie name
   */
  private static getCookieByName(name: string): string {
    const value = document.cookie.match('(^|;) ?' + name + '=([^;]*)(;|$)');
    return value ? value[2] : '';
  }
}
